import React, { createContext, useContext, ReactNode } from 'react';
import { useGetSchoolDetailQuery } from '../../Settings/Sessions/SessionsApi';
import { useUser } from './UserContext';
import { Features } from '../lib/constants';
import type { SubscriptionStatus, SubscriptionStatusInvoice } from '../types/types';

interface SchoolContextType {
  schoolDetail: any;
  isLoading: boolean;
  isError: boolean;
  error: any;
  hasFeature: (feature: string) => boolean;
  hasFeatures: (features: string[]) => boolean;
  refetch: () => void;
  subscriptionStatus: SubscriptionStatus | null | undefined;
  isSubscriptionLocked: boolean;
  isSubscriptionWarning: boolean;
  nextPaymentInvoice: SubscriptionStatusInvoice | null;
}

const SchoolContext = createContext<SchoolContextType | undefined>(undefined);

interface SchoolProviderProps {
  children: ReactNode;
}

export const SchoolProvider: React.FC<SchoolProviderProps> = ({ children }) => {
  const { isAuthenticated } = useUser();

  // Single instance of the query with proper caching
  const {
    data: schoolDetail,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetSchoolDetailQuery(
    {},
    {
      skip: !isAuthenticated, // Skip if not authenticated
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: true, // Only refetch on network reconnect
    }
  );

  // Memoized feature checking functions
  const hasFeature = (feature: string): boolean => {
    if (!schoolDetail?.features) return false;
    return schoolDetail.features.includes(feature);
  };

  const hasFeatures = (features: string[]): boolean => {
    if (!schoolDetail?.features) return false;
    return features.every(feature => schoolDetail.features.includes(feature));
  };

  const subscriptionStatus: SubscriptionStatus | null =
    schoolDetail?.subscriptionStatus ?? null;
  const isSubscriptionLocked = Boolean(subscriptionStatus?.locked);
  const isSubscriptionWarning = Boolean(
    subscriptionStatus?.warning && !subscriptionStatus?.locked
  );

  const unpaidInvoices: SubscriptionStatusInvoice[] =
    subscriptionStatus?.unpaidInvoices ?? [];
  const nextPaymentInvoice: SubscriptionStatusInvoice | null = unpaidInvoices.reduce<
    SubscriptionStatusInvoice | null
  >((soonest, invoice) => {
    if (!soonest || invoice.dueDate < soonest.dueDate) return invoice;
    return soonest;
  }, null);

  const value: SchoolContextType = {
    schoolDetail,
    isLoading,
    isError,
    error,
    hasFeature,
    hasFeatures,
    refetch,
    subscriptionStatus: subscriptionStatus ?? undefined,
    isSubscriptionLocked,
    isSubscriptionWarning,
    nextPaymentInvoice,
  };

  return (
    <SchoolContext.Provider value={value}>{children}</SchoolContext.Provider>
  );
};

// Custom hook to use the school context
export const useSchool = (): SchoolContextType => {
  const context = useContext(SchoolContext);
  if (context === undefined) {
    throw new Error('useSchool must be used within a SchoolProvider');
  }
  return context;
};

// Memoized selectors for specific data
export const useSchoolFeatures = () => {
  const { schoolDetail, hasFeature, hasFeatures } = useSchool();

  return {
    schoolDetail,
    hasBillingFeature: hasFeature(Features.BILLING_PAYMENT),
    hasAttendanceFeature: hasFeature(Features.ATTENDANCE),
    hasPayrollFeature: hasFeature(Features.PAYROLL),
    hasAcademicsFeature: hasFeature(Features.ACADEMICS),
    hasAllFeatures: (features: string[]) => hasFeatures(features),
  };
};

export const useSchoolSession = () => {
  const { schoolDetail, isLoading } = useSchool();

  return {
    currentSession: schoolDetail?.currentSession,
    isLoading,
  };
};
